import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Car, Coins, Download, Loader2, Mic, Plus, RotateCcw, Star, Upload, Music2 } from "lucide-react";
import { api, errorMessage, BACKEND_URL, mediaSrc, fmtDuration, uploadFileChunked } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { CarStepper } from "@/components/caredits/CarStepper";
import { ClipCard } from "@/components/caredits/ClipCard";
import { TemplateTile } from "@/components/caredits/TemplateTile";
import { MusicRow } from "@/components/caredits/MusicRow";
import { VoiceoverPanel } from "@/components/caredits/VoiceoverPanel";

const STEPS = ["Clips", "Template", "Music", "Create"];
const ACCEPT = "video/mp4,video/quicktime,video/webm,video/x-matroska,.mp4,.mov,.webm,.mkv,.m4v";
const lenLabel = (l) => (l < 60 ? `${l}s` : l % 60 === 0 ? `${l / 60}m` : `${Math.floor(l / 60)}m${l % 60}s`);

export default function CarEdits() {
  const { user, refreshUser } = useAuth();
  const [options, setOptions] = useState(null);
  const [edit, setEdit] = useState(null);
  const [step, setStep] = useState(1);
  const [tagOverrides, setTagOverrides] = useState({});
  const [template, setTemplate] = useState(null);
  const [length, setLength] = useState(30);
  const [musicTrack, setMusicTrack] = useState(null);
  const [customMusic, setCustomMusic] = useState(null);
  const [heroId, setHeroId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const fileRef = useRef(null);
  const audioRef = useRef(null);
  const musicFileRef = useRef(null);
  const [playing, setPlaying] = useState(null);
  const pollTimer = useRef(null);

  useEffect(() => { api.get("/car-edits/options").then((r) => setOptions(r.data)).catch(() => {}); }, []);
  useEffect(() => () => { clearTimeout(pollTimer.current); audioRef.current?.pause(); }, []);

  const clips = useMemo(() => edit?.clips || [], [edit]);
  const cost = options?.credit_cost ?? 5;
  const tagOf = (c) => tagOverrides[c.clip_id] ?? c.tag;
  const isDraft = !edit || edit.status === "draft" || edit.status === "failed";
  const rendering = edit && ["queued", "processing"].includes(edit.status);
  const done = edit?.status === "rendered";

  const ensureEdit = useCallback(async () => {
    if (edit) return edit;
    const { data } = await api.post("/car-edits");
    setEdit(data);
    return data;
  }, [edit]);

  const addFiles = async (files) => {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    let current = await ensureEdit();
    const room = options.max_clips - (current.clips?.length || 0);
    if (list.length > room) toast.warning(`Only ${room} more clip${room === 1 ? "" : "s"} can be added`);
    setUploading(true);
    for (const file of list.slice(0, room)) {
      try {
        const uploadId = await uploadFileChunked(file);
        const { data } = await api.post(`/car-edits/${current.id}/clips/from-upload`, { upload_id: uploadId, tag: "action" });
        current = data;
        setEdit(data);
      } catch (e) {
        toast.error(errorMessage(e, `Could not add ${file.name}`));
      }
    }
    setUploading(false);
  };

  const setTag = (clipId, tag) => setTagOverrides((o) => ({ ...o, [clipId]: tag }));
  const removeClip = async (clipId) => {
    try {
      const { data } = await api.delete(`/car-edits/${edit.id}/clips/${clipId}`);
      setEdit(data);
      if (heroId === clipId) setHeroId(null);
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const togglePlay = (trackId) => {
    const a = audioRef.current;
    if (!a) return;
    if (playing === trackId) { a.pause(); setPlaying(null); return; }
    if (a.src !== `${BACKEND_URL}/api/car-edits/music/${trackId}/preview`) {
      a.src = `${BACKEND_URL}/api/car-edits/music/${trackId}/preview`;
      a.load();
    }
    setPlaying(trackId);
    a.play().catch((err) => {
      // Ignore the harmless AbortError that fires when switching tracks quickly.
      if (err?.name !== "AbortError") { setPlaying(null); toast.error("Could not play preview"); }
    });
  };

  const uploadMusic = async (file) => {
    if (!file) return;
    try {
      const { data } = await api.post(`/car-edits/${edit.id}/music`, (() => { const f = new FormData(); f.append("file", file); return f; })());
      setEdit(data);
      setCustomMusic(file.name);
      setMusicTrack(null);
      toast.success("Custom track added");
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const saveVoiceover = async (text, voice) => {
    try {
      const { data } = await api.post(`/car-edits/${edit.id}/voiceover`, { text, voice });
      setEdit(data);
      toast.success("Voiceover added");
    } catch (e) { toast.error(errorMessage(e)); }
  };
  const clearVoiceover = async () => {
    try { const { data } = await api.delete(`/car-edits/${edit.id}/voiceover`); setEdit(data); }
    catch (e) { toast.error(errorMessage(e)); }
  };

  const create = async () => {
    setCreating(true);
    try {
      const tags = Object.fromEntries(clips.map((c) => [c.clip_id, tagOf(c)]));
      const { data } = await api.post(`/car-edits/${edit.id}/render`, {
        template, length, music_track_id: customMusic ? null : musicTrack, hero_clip_id: heroId, tags,
      });
      setEdit(data);
      refreshUser();
      toast.success("Building your edit…");
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setCreating(false);
    }
  };

  const reset = () => {
    clearTimeout(pollTimer.current);
    setEdit(null); setStep(1); setTagOverrides({}); setTemplate(null); setLength(30);
    setMusicTrack(null); setCustomMusic(null); setHeroId(null);
  };

  const download = async () => {
    try {
      const { data } = await api.get(`/car-edits/${edit.id}/download`);
      window.open(`${BACKEND_URL}${data.url}`, "_blank");
    } catch (e) { toast.error(errorMessage(e)); }
  };

  useEffect(() => {
    if (!rendering) return;
    pollTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/car-edits/${edit.id}`);
        setEdit(data);
        if (data.status === "failed") toast.error(data.error || "Render failed");
      } catch { /* retry next tick */ }
    }, 3000);
    return () => clearTimeout(pollTimer.current);
  }, [rendering, edit]);

  const suggestedFor = (trackId) => options?.music.find((m) => m.id === trackId)?.suggested?.includes(template);
  const canNext = useMemo(() => {
    if (step === 1) return clips.length >= (options?.min_clips || 3);
    if (step === 2) return !!template;
    if (step === 3) return !!(musicTrack || customMusic);
    return true;
  }, [step, clips, options, template, musicTrack, customMusic]);

  if (!options) return <div className="h-64 rounded-2xl shimmer" />;

  if (rendering || done) {
    return (
      <div className="max-w-xl mx-auto space-y-6 rise" data-testid="car-result">
        <div className="text-center">
          <span className="overline-label">Car Edits</span>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">{done ? "Your edit is ready" : "Building your edit"}</h1>
        </div>
        <div className="card-surface p-4">
          {done ? (
            <video src={`${BACKEND_URL}${edit.preview_url}`} poster={mediaSrc(edit.thumb_url)} controls playsInline className="w-full rounded-xl bg-black aspect-[9/16] max-h-[70vh] mx-auto" data-testid="car-preview-video" />
          ) : (
            <div className="aspect-[9/16] max-h-[60vh] mx-auto rounded-xl bg-black/70 grid place-items-center">
              <div className="flex flex-col items-center gap-3 text-muted-foreground">
                <Loader2 className="animate-spin text-primary" size={30} />
                <p className="text-sm font-mono">{edit.status === "queued" ? "Queued…" : "Cutting & syncing to the beat…"}</p>
              </div>
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-3 justify-center">
          {done && <Button onClick={download} className="btn-pill h-11 px-6" data-testid="car-download-button"><Download size={15} className="mr-2" /> Download edit</Button>}
          <Button variant={done ? "outline" : "ghost"} onClick={reset} className="btn-pill h-11 px-6" data-testid="car-new-edit-button"><Plus size={15} className="mr-2" /> New edit</Button>
        </div>
        {done && <p className="text-center text-xs text-muted-foreground font-mono">{fmtDuration(edit.output_duration)} · {options.templates.find((t) => t.id === edit.template)?.label} · {edit.music?.name}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-6 rise" data-testid="car-edits-page">
      <audio ref={audioRef} onEnded={() => setPlaying(null)} className="hidden" />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="h-11 w-11 rounded-xl bg-primary/12 text-primary grid place-items-center"><Car size={22} /></span>
          <div>
            <span className="overline-label">Car Edits</span>
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">A few taps to a reel</h1>
          </div>
        </div>
        <div className="flex items-center gap-1.5 rounded-xl border border-border bg-card px-3 h-9 text-sm font-mono" data-testid="car-credit-cost"><Coins size={14} className="text-primary" /> {cost} credits / edit</div>
      </div>

      <CarStepper steps={STEPS} current={step} onJump={setStep} />

      {/* STEP 1 — clips & tags */}
      {step === 1 && (
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <div><h2 className="text-lg font-semibold">Upload your clips</h2><p className="text-sm text-muted-foreground">Add {options.min_clips}–{options.max_clips} short clips, then tag each one.</p></div>
            <span className="text-sm font-mono text-muted-foreground" data-testid="car-clip-count">{clips.length}/{options.max_clips}</span>
          </div>
          <input ref={fileRef} type="file" accept={ACCEPT} multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} data-testid="car-file-input" />
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
            {clips.map((c) => (
              <ClipCard key={c.clip_id} clip={c} tags={options.tags} tag={tagOf(c)} onTag={setTag} onRemove={removeClip} disabled={!isDraft} />
            ))}
            {clips.length < options.max_clips && (
              <button onClick={() => fileRef.current?.click()} disabled={uploading} className="aspect-[9/16] rounded-2xl border-2 border-dashed border-border hover:border-primary/60 grid place-items-center text-muted-foreground transition-colors" data-testid="car-add-clip-button">
                {uploading ? <Loader2 className="animate-spin" size={22} /> : <span className="flex flex-col items-center gap-1 text-xs"><Upload size={22} /> Add clip</span>}
              </button>
            )}
          </div>
          {clips.length > 0 && clips.length < options.min_clips && <p className="text-xs text-primary font-mono">Add at least {options.min_clips - clips.length} more clip{options.min_clips - clips.length === 1 ? "" : "s"}.</p>}
        </div>
      )}

      {/* STEP 2 — template */}
      {step === 2 && (
        <div className="space-y-5">
          <div><h2 className="text-lg font-semibold">Pick a style</h2><p className="text-sm text-muted-foreground">Sets the pacing, cut rhythm and look of your edit.</p></div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="template-grid">
            {options.templates.map((t) => <TemplateTile key={t.id} tpl={t} selected={template === t.id} onSelect={setTemplate} />)}
          </div>
        </div>
      )}

      {/* STEP 3 — music & length */}
      {step === 3 && (
        <div className="space-y-6">
        <div className="grid lg:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div><h2 className="text-lg font-semibold">Add music</h2><p className="text-sm text-muted-foreground">Pick a track or upload your own audio.</p></div>
            <div className="space-y-2" data-testid="music-list">
              {[...options.music].sort((a, b) => (suggestedFor(b.id) ? 1 : 0) - (suggestedFor(a.id) ? 1 : 0)).map((t) => (
                <MusicRow key={t.id} track={t} selected={musicTrack === t.id && !customMusic} playing={playing === t.id} suggested={suggestedFor(t.id)}
                  onSelect={(id) => { setMusicTrack(id); setCustomMusic(null); }} onTogglePlay={togglePlay} />
              ))}
            </div>
            <input ref={musicFileRef} type="file" accept="audio/*,.mp3,.m4a,.wav,.aac,.ogg" className="hidden" onChange={(e) => { uploadMusic(e.target.files?.[0]); e.target.value = ""; }} data-testid="car-music-input" />
            <button onClick={() => musicFileRef.current?.click()} className={`w-full flex items-center gap-3 rounded-xl border p-3 transition-colors ${customMusic ? "border-primary bg-primary/10" : "border-dashed border-border hover:border-primary/50"}`} data-testid="car-upload-music-button">
              <span className="h-9 w-9 rounded-full bg-primary/15 text-primary grid place-items-center"><Music2 size={15} /></span>
              <span className="text-sm font-semibold truncate">{customMusic || "Upload your own audio"}</span>
            </button>
          </div>
          <div className="space-y-4">
            <div className="flex items-end justify-between">
              <div><h2 className="text-lg font-semibold">Length</h2><p className="text-sm text-muted-foreground">How long should the reel be?</p></div>
              <span className="text-2xl font-bold tabular-nums" data-testid="length-value">{lenLabel(length)}</span>
            </div>
            <Slider
              min={options.min_length ?? 10}
              max={options.max_length ?? 300}
              step={5}
              value={[length]}
              onValueChange={(v) => setLength(v[0])}
              className="py-2"
              data-testid="length-slider"
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground font-mono">
              <span>{lenLabel(options.min_length ?? 10)}</span><span>{lenLabel(options.max_length ?? 300)}</span>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="length-presets">
              {options.lengths.map((l) => (
                <button key={l} onClick={() => setLength(l)} className={`btn-pill px-4 py-1.5 text-sm border transition-all ${length === l ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary/50"}`} data-testid={`length-${l}`}>
                  {lenLabel(l)}
                </button>
              ))}
            </div>
          </div>
        </div>
          <VoiceoverPanel voices={options.voices} voiceover={edit?.voiceover} onSave={saveVoiceover} onClear={clearVoiceover} disabled={!isDraft} />
        </div>
      )}

      {/* STEP 4 — hero + create */}
      {step === 4 && (
        <div className="space-y-6">
          <div><h2 className="text-lg font-semibold">Mark the hero clip <span className="text-muted-foreground font-normal text-sm">(optional)</span></h2><p className="text-sm text-muted-foreground">We'll land this shot on the beat drop. Skip it and the template decides.</p></div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3" data-testid="hero-grid">
            {clips.map((c) => (
              <button key={c.clip_id} onClick={() => setHeroId(heroId === c.clip_id ? null : c.clip_id)} className={`relative aspect-[9/16] rounded-xl overflow-hidden bg-black/60 transition-all ${heroId === c.clip_id ? "ring-2 ring-primary" : "opacity-80 hover:opacity-100"}`} data-testid={`hero-select-${c.clip_id}`}>
                {c.thumb_url && <img src={mediaSrc(c.thumb_url)} alt="" className="absolute inset-0 w-full h-full object-cover" />}
                <span className={`absolute top-1.5 right-1.5 h-6 w-6 rounded-full grid place-items-center ${heroId === c.clip_id ? "bg-primary text-primary-foreground" : "bg-black/60 text-white"}`}><Star size={12} fill={heroId === c.clip_id ? "currentColor" : "none"} /></span>
              </button>
            ))}
          </div>
          <div className="card-surface p-5 space-y-3" data-testid="car-review">
            <h3 className="text-sm font-semibold">Review</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div><p className="overline-label">Clips</p><p className="font-semibold mt-1">{clips.length}</p></div>
              <div><p className="overline-label">Template</p><p className="font-semibold mt-1">{options.templates.find((t) => t.id === template)?.label || "—"}</p></div>
              <div><p className="overline-label">Music</p><p className="font-semibold mt-1 truncate">{customMusic || options.music.find((m) => m.id === musicTrack)?.name || "—"}</p></div>
              <div><p className="overline-label">Length</p><p className="font-semibold mt-1">{lenLabel(length)}</p></div>
            </div>
            {edit?.voiceover && <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Mic size={12} className="text-primary" /> Voiceover ({edit.voiceover.voice}): "{edit.voiceover.text.slice(0, 60)}{edit.voiceover.text.length > 60 ? "…" : ""}"</p>}
            <Button onClick={create} disabled={creating || (user?.credits ?? 0) < cost} className="btn-pill h-12 w-full text-base" data-testid="car-create-button">
              {creating ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              Create edit · {cost} credits
            </Button>
            {(user?.credits ?? 0) < cost && <p className="text-xs text-destructive text-center">Not enough credits — you have {user?.credits ?? 0}.</p>}
          </div>
        </div>
      )}

      {/* nav */}
      <div className="flex items-center justify-between pt-2">
        <Button variant="ghost" className="btn-pill" disabled={step === 1} onClick={() => setStep((s) => s - 1)} data-testid="car-back-button"><ArrowLeft size={15} className="mr-1" /> Back</Button>
        {step < 4 && <Button className="btn-pill px-6" disabled={!canNext} onClick={() => setStep((s) => s + 1)} data-testid="car-next-button">Next <ArrowRight size={15} className="ml-1" /></Button>}
        {step === 4 && <Button variant="ghost" className="btn-pill" onClick={reset} data-testid="car-reset-button"><RotateCcw size={14} className="mr-1" /> Start over</Button>}
      </div>
    </div>
  );
}
